import { useWorkflow } from 'features/libra/WorkflowContext'
import { createContext, FC, useCallback, useContext, useState } from 'react'
import { RolodexEntryTagsType } from 'types'

import { LibraTicket, UpdateTicketArgs } from './libraTicketsApi'

export type TicketUpdateContextType = {
  advanceTicketState: () => Promise<void>
  fieldValues: Record<string, unknown>
  setFieldValue: (fieldId: string, value: unknown) => Promise<void>
  setFieldValues: (values: Record<string, unknown>) => Promise<void>
  isLoading: boolean
  ticketTags: RolodexEntryTagsType
}

export const TicketUpdateContext = createContext<TicketUpdateContextType>({
  advanceTicketState: () => Promise.resolve(),
  fieldValues: {},
  setFieldValue: () => Promise.resolve(),
  setFieldValues: () => Promise.resolve(),
  isLoading: false,
  ticketTags: {},
})

export const TicketUpdateProvider: FC<{
  children: React.ReactNode
  ticket: LibraTicket
  isLoading: boolean
  doUpdateTicket: (args: UpdateTicketArgs) => Promise<void>
}> = ({ children, ticket, doUpdateTicket, isLoading }) => {
  const workflow = useWorkflow()
  const [isSaving, setIsSaving] = useState(false)
  const advanceTicketState = useCallback(async () => {
    setIsSaving(true)
    try {
      const currentStepIndex = workflow.stepIndices.get(ticket.stepId) ?? -1
      const nextIndex = currentStepIndex + 1

      if (nextIndex === workflow.steps.length) {
        await doUpdateTicket({
          ticketId: ticket.id,
          workflowComplete: true,
        })
        return
      }

      const nextStep = workflow.steps[nextIndex]
      if (!nextStep) {
        console.warn('No next step to advance to')
        return
      }
      await doUpdateTicket({
        ticketId: ticket.id,
        stepId: nextStep.id,
      })
    } finally {
      setIsSaving(false)
    }
  }, [
    doUpdateTicket,
    ticket.id,
    ticket.stepId,
    workflow.stepIndices,
    workflow.steps,
  ])

  const setFieldValues = useCallback(
    async (values: Record<string, unknown>) => {
      setIsSaving(true)
      try {
        await doUpdateTicket({
          ticketId: ticket.id,
          stepId: ticket.stepId,
          fieldValues: { ...ticket.fieldValues, ...values },
        })
      } finally {
        setIsSaving(false)
      }
    },
    [doUpdateTicket, ticket.id, ticket.stepId, ticket.fieldValues]
  )

  const setFieldValue = useCallback(
    async (fieldId: string, value: unknown) => {
      await setFieldValues({ [fieldId]: value })
    },
    [setFieldValues]
  )

  const { fieldValues, tags: ticketTags } = ticket

  return (
    <TicketUpdateContext.Provider
      value={{
        isLoading: isLoading || isSaving,
        advanceTicketState,
        fieldValues,
        setFieldValue,
        setFieldValues,
        ticketTags,
      }}>
      {children}
    </TicketUpdateContext.Provider>
  )
}

export const useTicketUpdate = () => {
  const ctx = useContext(TicketUpdateContext)
  if (!ctx) {
    throw new Error(
      'useTicketUpdate must be used within a TicketUpdateProvider'
    )
  }
  return ctx
}
